import { useCallback, useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Button,
  ConfigProvider,
  Layout,
  Menu,
  Segmented,
  Space,
  Typography,
  theme,
} from "antd";
import enUS from "antd/locale/en_US";
import zhCN from "antd/locale/zh_CN";
import {
  CommentOutlined,
  DashboardOutlined,
  MessageOutlined,
  MoonOutlined,
  RiseOutlined,
  SunOutlined,
  ThunderboltOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import type { Language, ThemeMode } from "./types";
import { textMap } from "./i18n";
import { syncDocumentSeo } from "./utils/seoDocument";
import { UiStateContext } from "./state";
import { fetchCurrentUser, type CurrentUser } from "./services/compliance";
import { HomePage } from "./pages/HomePage";
import { ChatPage } from "./pages/ChatPage";
import { LoginPage } from "./pages/LoginPage";
import { BillingPage } from "./pages/BillingPage";
import { DashboardPage } from "./pages/DashboardPage";
import { AgreementPage } from "./pages/AgreementPage";
import { UpgradePlanPage } from "./pages/UpgradePlanPage";
import { FeedbackPage } from "./pages/FeedbackPage";
import { StudioStandalonePage } from "./pages/StudioStandalonePage";
import { AppHeaderLogo } from "./components/AppHeaderLogo";
import { AuthSessionExpiredNotifier } from "./components/AuthSessionExpiredNotifier";
import { GlobalApiLoading } from "./components/GlobalApiLoading";
import { GlobalUserMenu } from "./components/GlobalUserMenu";

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

function App() {
  const [lang, setLang] = useState<Language>("en");
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
    return "light";
  });
  const [followSystemTheme, setFollowSystemTheme] = useState(true);
  const [authed, setAuthed] = useState<boolean>(
    () => !!localStorage.getItem("xtool_token"),
  );
  const navigate = useNavigate();
  const location = useLocation();
  const t = textMap[lang];
  const isDark = themeMode === "dark";

  const isChatRoute = location.pathname === "/chat";
  const isStudioRoute =
    location.pathname === "/studio" || location.pathname.startsWith("/studio/");
  const isWorkspaceRoute = location.pathname.startsWith("/app");
  const isMarketingRoute = location.pathname === "/";
  const publicHeaderVisible = false;

  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  const workspaceItems = useMemo(
    () => [

      {
        key: "/app/dashboard",
        label: t.navDashboard,
        icon: <DashboardOutlined />,
      },
      { key: "/app/billing", label: t.navBilling, icon: <WalletOutlined /> },
      {
        key: "/app/upgrade",
        label: lang === "zh" ? "升级计划" : "Upgrade",
        icon: <RiseOutlined />,
      },
      { key: "/app/feedback", label: t.navFeedback, icon: <MessageOutlined /> },
      {
        key: "/chat",
        label: lang === "zh" ? "审查对话" : "Chat/Review",
        icon: <CommentOutlined />,
      },
      {
        key: "/studio",
        className: "console-menu-studio-item",
        icon: <ThunderboltOutlined />,
        label: t.navEnterStudio,
      },
    ],
    [lang, t.navDashboard, t.navBilling, t.navFeedback, t.navEnterStudio],
  );

  useEffect(() => {
    if (!authed || !isWorkspaceRoute) return;
    let mounted = true;
    fetchCurrentUser().then((me) => {
      if (mounted) setCurrentUser(me);
    });
    return () => {
      mounted = false;
    };
  }, [authed, isWorkspaceRoute]);

  const selectedWorkspaceKey =
    location.pathname === "/app"
      ? "/app/dashboard"
      : location.pathname === "/chat"
        ? "/chat"
        : location.pathname.startsWith("/studio")
          ? "/studio"
          : location.pathname;

  const handleLogin = useCallback(
    (token: string) => {
      localStorage.setItem("xtool_token", token);
      setAuthed(true);
      navigate("/app/dashboard");
    },
    [navigate],
  );

  const handleLogout = useCallback(() => {
    localStorage.removeItem("xtool_token");
    setAuthed(false);
    navigate("/");
  }, [navigate]);

  const clearAuthSession = useCallback(() => {
    localStorage.removeItem("xtool_token");
    setAuthed(false);
  }, []);

  const contextValue = useMemo(
    () => ({
      lang,
      themeMode,
      setLang: (next: Language) => setLang(next),
      toggleLang: () => setLang((prev) => (prev === "zh" ? "en" : "zh")),
      toggleTheme: (nextDark: boolean) => {
        setFollowSystemTheme(false);
        setThemeMode(nextDark ? "dark" : "light");
      },
      authed,
      logout: handleLogout,
      clearAuthSession,
    }),
    [lang, themeMode, authed, handleLogout, clearAuthSession],
  );

  useEffect(() => {
    syncDocumentSeo(lang);
  }, [lang]);

  useEffect(() => {
    if (!followSystemTheme || typeof window === "undefined") {
      return;
    }
    const matcher = window.matchMedia("(prefers-color-scheme: dark)");
    const applyTheme = (matches: boolean) =>
      setThemeMode(matches ? "dark" : "light");
    applyTheme(matcher.matches);
    const handler = (event: MediaQueryListEvent) => applyTheme(event.matches);
    matcher.addEventListener("change", handler);
    return () => matcher.removeEventListener("change", handler);
  }, [followSystemTheme]);

  return (
    <ConfigProvider
      locale={lang === "zh" ? zhCN : enUS}
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: "#246BFD",
          borderRadius: 14,
          fontFamily:
            "Inter, Segoe UI, PingFang SC, Microsoft YaHei, sans-serif",
        },
      }}
    >
      <AntdApp>
        <UiStateContext.Provider value={contextValue}>
          <AuthSessionExpiredNotifier />
          <>
          <Layout className={`app-shell ${isDark ? "dark" : "light"}`}>
            {publicHeaderVisible ? (
              <Header className="top-bar">
                <Space
                  size={16}
                  align="center"
                  className="brand-link"
                  onClick={() => navigate("/")}
                >
                  <AppHeaderLogo className="brand-logo-img" />
                  <div className="brand-wrap">
                    <Text className="logo-text">{t.productName}</Text>
                    <Text className="brand-badge">{t.headerBadge}</Text>
                  </div>
                </Space>

                <Space size={12}>
                  <Segmented
                    className="lang-bubble"
                    value={lang}
                    options={[
                      { label: "EN", value: "en" },
                      { label: "中文", value: "zh" },
                    ]}
                    onChange={(value) => setLang(value as Language)}
                  />
                  <Button
                    className="icon-btn-pill icon-btn-pill-circle"
                    icon={isDark ? <SunOutlined /> : <MoonOutlined />}
                    onClick={() => contextValue.toggleTheme(!isDark)}
                  />
                  <Button
                    type="primary"
                    onClick={() =>
                      navigate(authed ? "/app/dashboard" : "/login")
                    }
                  >
                    {authed ? t.openConsole : t.loginEntry}
                  </Button>
                </Space>
              </Header>
            ) : null}

            <Content
              className={
                isWorkspaceRoute
                  ? "content-wrap workspace-wrap"
                  : isStudioRoute
                    ? "content-wrap studio-route-wrap"
                    : isChatRoute
                      ? "content-wrap home-chat-wrap"
                      : isMarketingRoute
                        ? "content-wrap marketing-wrap"
                        : "content-wrap"
              }
            >
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route
                  path="/chat"
                  element={authed ? <ChatPage /> : <Navigate to="/" replace />}
                />
                <Route
                  path="/studio/*"
                  element={
                    authed ? (
                      <StudioStandalonePage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/login"
                  element={
                    authed ? (
                      <Navigate to="/app/dashboard" replace />
                    ) : (
                      <LoginPage onLogin={handleLogin} />
                    )
                  }
                />
                <Route path="/agreement" element={<AgreementPage />} />
                <Route
                  path="/app/*"
                  element={
                    authed ? (
                      <Layout className="console-shell">
                        <Sider
                          width={256}
                          className="console-sider console-sider-personal"
                        >
                          <div
                            className="console-brand brand-link"
                            onClick={() => navigate("/")}
                          >
                            <div>
                              <Text className="console-brand-title-personal">
                                XTool Compliance
                              </Text>
                              <Text className="console-brand-subtitle-personal">
                                {lang === "zh"
                                  ? "架构哨兵"
                                  : "Architectural Sentinel"}
                              </Text>
                            </div>
                          </div>
                          <Menu
                            mode="inline"
                            items={workspaceItems}
                            selectedKeys={[selectedWorkspaceKey]}
                            onClick={(item) => navigate(item.key)}
                            className="console-menu console-menu-personal"
                          />
                          <div className="console-user-block">
                            <div className="console-user-avatar">
                              <img
                                src="https://lh3.googleusercontent.com/aida-public/AB6AXuBi3Bb_6lG34Q2I31fmeIrFCqvoKYp8VqNdrlEWzuCvFFYNzfN6lmfk3t63-ALshFbLRrhBtJOnnFYBLAM_IZgUYsJ7zJ0C1cCZkw8y77Gnn8Vjb-vLF0-TdoDp-c9eknk-ngG6eq-r_4GIgfQu-k-DeXchqcBGDiNj55qi3bB55NWNmN9Bk8pCQlJQugYj8bnVgiZXCn3XtmpGYPZ8KKLXd1Hg_MP5IHD4JisJJGpgScn9Dhm9fsZ3qM6JhD6Rn7M4CVtPgtfQRCam"
                                alt="avatar"
                              />
                            </div>
                            <div className="console-user-info">
                              <Text className="console-user-name">
                                {currentUser?.email?.split("@")[0] ?? "User"}
                              </Text>
                              <Text className="console-user-role">
                                {currentUser?.role ??
                                  (lang === "zh" ? "用户" : "User")}
                              </Text>
                            </div>
                          </div>
                        </Sider>
                        <Layout>
                          <Header className="console-header">
                            <div
                              className="console-header-brand brand-link"
                              onClick={() => navigate("/")}
                              role="button"
                            >
                              <AppHeaderLogo />
                              <span className="console-header-brand-label">
                                XTool
                              </span>
                            </div>
                            <Space>
                              <Segmented
                                className="lang-bubble"
                                value={lang}
                                options={[
                                  { label: "EN", value: "en" },
                                  { label: "中文", value: "zh" },
                                ]}
                                onChange={(value) => setLang(value as Language)}
                              />
                              <Button
                                className="icon-btn-pill icon-btn-pill-circle"
                                icon={
                                  isDark ? <SunOutlined /> : <MoonOutlined />
                                }
                                onClick={() =>
                                  contextValue.toggleTheme(!isDark)
                                }
                              />
                              <GlobalUserMenu />
                            </Space>
                          </Header>
                          <Content className="console-content">
                            <Routes>
                              <Route
                                path="/"
                                element={<Navigate to="dashboard" replace />}
                              />
                              <Route
                                path="dashboard"
                                element={<DashboardPage />}
                              />
                              <Route path="billing" element={<BillingPage />} />
                              <Route
                                path="feedback"
                                element={<FeedbackPage />}
                              />
                              <Route
                                path="upgrade"
                                element={<UpgradePlanPage />}
                              />
                              <Route
                                path="compliance-hub"
                                element={<Navigate to="/studio" replace />}
                              />
                              <Route
                                path="studio"
                                element={<Navigate to="/studio" replace />}
                              />
                              <Route
                                path="*"
                                element={<Navigate to="dashboard" replace />}
                              />
                            </Routes>
                          </Content>
                        </Layout>
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Content>
          </Layout>
          <GlobalApiLoading />
          </>
        </UiStateContext.Provider>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
